import React, { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  TextField,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  styled,
  Button,
  Stack,
  Chip,
  Switch,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  alpha,
  CircularProgress,
  Alert,
  Typography,
  ListItemIcon,
} from "@mui/material";
import {
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";

import { TableListStyled } from "../styles/table.style";
import FilterIconSvg from "../../../assets/images/icon-filter.svg";
import FilterSelectIconSvg from "../../../assets/images/icon-filter-select.svg";
import DeleteIconSvg from "../../../assets/images/icon-delete.svg";
import DownloadIconSvg from "../../../assets/images/icon-download.svg";
import SensitiveIconSvg from "../../../assets/images/icon-sensitive.svg";
import SensitiveIconGraySvg from "../../../assets/images/icon-sensitiveGray.svg";
import NonSensitiveIconSvg from "../../../assets/images/icon-non-sensitive.svg";
import EditIconSvg from "../../../assets/images/icon-edit.svg";
import SearchIcon from "@mui/icons-material/Search";
import { MenuProps } from "@mui/material/Menu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";

import {
  CLIENT_STATUS,
  getFormattedDataTime,
  getUserRoles,
} from "../../helpers/constants";
import FileUpload from "../modals/FileUpload";
import SnackbarComponent from "./SnackBarComponent";
import SnackbarSuccessComponent from "./SnackBarSuccessComponent";
import theme, { colors, media } from "../../../assets/styles/theme";
import DownloadIcon from "@mui/icons-material/Download";

interface TableState {
  orderBy: any;
  order: "asc" | "desc";
  page: number;
  rowsPerPage: number;
  filters: Record<string, any>;
  groupBy: any;
  globalSearch: string;
  columnFilters: Record<string, any>;
  checkboxFilters: any;
  columnSearchTerms: Record<string, any>;
  showUnassigned: any;
}

interface Column {
  value: string;
  label: string;
  numeric: boolean;
  date: boolean;
  showFilter: boolean;
  showColumn: boolean;
}

interface TableProps {
  data: any[];
  onRowClick: (item: any) => void;
  handelAction: (item: any, type: any, status: any) => void;
  isGroupingEnabled: boolean;
  isExportEnabled: boolean;
  columns: Column[];
  defaultSortColumn?: any;
  defaultSortDirection?: any;
  rowsPerPageOptions?: any;
  storageKey: any;
  loading: any;
  showFileUploadModal: any;
  placeholder: any;
  isFixedColumn: any;
  isFixedRightColumn: any;
  exportFileName: any;
  isEditMode: any;
  editData: any;
  handleFilePopUpClose: () => void;
  handleAttachmentsDownload: (item: any) => void;
  fromScreen: any;
  industryOptions: any;
  uploadType: any;
  handleSaveFormSubmission: () => void;
  showUnassignedToggle: any;
}

interface GroupedData {
  [key: string]: {
    items: any[];
    isExpanded: boolean;
  };
}

// Add new interface for global search
interface OriginalGridState {
  globalSearch: string;
  columnFilters: Partial<Record<keyof any, string>>;
}

// Custom styled components for fixed column
const StyledTableContainer = styled(TableContainer)({
  position: "relative",
  maxHeight: "calc(100vh - 260px)",
  minHeight: "calc(100vh - 260px)",
  paddingBottom: "4px",
  overflow: "auto",
  "& .MuiTableCell-root": {
    whiteSpace: "nowrap",
  },
  [theme.breakpoints.up(2160)]: {
    maxHeight: "calc(100vh - 280px)",
    minHeight: "485px"
  },
});

const FixedTableCell = styled(TableCell)({
  position: "sticky",
  right: 0,
  backgroundColor: "white",
  zIndex: 2,
  // boxShadow: "-2px 0px 4px rgba(0, 0, 0, 0.1)",
  borderLeft: "solid 1px rgba(0,0,0,0.12)",
  "&.MuiTableCell-head": {
    backgroundColor: "#fff",
    zIndex: 3,
  },
});

const initialFileValues = {
  client: {
    label: "",
    value: "",
  },
  industry: "",
  testAndLearnVehicle: "",
  title: "",
  hypothesis: "",
  background: "",
  testStoresOrGeography: "",
  controlStoresOrGeography: "",
  measuredKPI: "",
  testDesign: "",
  testStartDate: null,
  testEndDate: null,
  results: "",
  statisticalSignificance: "",
  recommendationsOrNotes: "",
  uploadType: "",
};

const OriginalGrid: React.FC<TableProps> = ({
  data,
  onRowClick,
  isGroupingEnabled,
  isExportEnabled,
  columns,
  defaultSortColumn,
  defaultSortDirection,
  rowsPerPageOptions,
  storageKey,
  loading,
  showFileUploadModal,
  placeholder,
  isFixedColumn,
  isFixedRightColumn,
  exportFileName,
  handelAction,
  isEditMode,
  editData,
  handleFilePopUpClose,
  handleAttachmentsDownload,
  fromScreen,
  industryOptions,
  uploadType,
  handleSaveFormSubmission,
  showUnassignedToggle,
}) => {
  const STORAGE_KEY = storageKey;

  // file Upload modal related
  const label = { inputProps: { "aria-label": "Status " } };

  const StyledMenu = styled((props: MenuProps) => (
    <Menu
      elevation={0}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      {...props}
    />
  ))(({ theme }) => ({
    ".MuiButton-endIcon": {
      // border: 'solid 1px red',
    },
    "& .MuiPaper-root": {
      borderRadius: 6,
      marginTop: theme.spacing(1),
      minWidth: 180,
      color: "rgb(55, 65, 81)",
      boxShadow:
        "0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
      "& .MuiMenu-list": {
        padding: "4px 0",
      },

      "& .MuiMenuItem-root": {
        "& .MuiSvgIcon-root": {
          fontSize: 18,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        "&:active": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            theme.palette.action.selectedOpacity
          ),
        },
      },
      ...theme.applyStyles("dark", {
        color: theme.palette.grey[300],
      }),
    },
  }));

  const queryClient = useQueryClient();

  const loadInitialState = (): TableState => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      return JSON.parse(savedState);
    }
    return {
      orderBy: defaultSortColumn,
      order: defaultSortDirection,
      page: 0,
      rowsPerPage: rowsPerPageOptions[0],
      filters: {},
      groupBy: "",
      globalSearch: "",
      columnFilters: {},
      checkboxFilters: {},
      columnSearchTerms: {},
      showUnassigned: false,
    };
  };

  // Calculate widths for columns
  const totalColumns = columns.filter((col) => col.showColumn).length;
  const lastColumnWidth = "200px";
  const remainingColumnsWidth = `calc((100% - ${lastColumnWidth}) / ${
    totalColumns - 1
  })`;

  // States for sorting
  const [orderBy, setOrderBy] = useState<keyof any>(loadInitialState().orderBy);
  const [order, setOrder] = useState<"asc" | "desc">(loadInitialState().order);

  // States for pagination
  const [page, setPage] = useState(loadInitialState().page);
  const [rowsPerPage, setRowsPerPage] = useState(
    loadInitialState().rowsPerPage
  );

  // States for filtering
  const [filters, setFilters] = useState<Partial<Record<keyof any, string>>>(
    loadInitialState().filters
  );
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [activeFilter, setActiveFilter] = useState<keyof any | null>(null);

  // States for grouping
  const [groupBy, setGroupBy] = useState<keyof any | "">(
    loadInitialState().groupBy
  );
  const [groupedData, setGroupedData] = useState<GroupedData>({});

  // Separate global search from column filters
  const [searchState, setSearchState] = useState<OriginalGridState>({
    globalSearch: loadInitialState().globalSearch,
    columnFilters: loadInitialState().columnFilters,
  });

  const [checkboxFilters, setCheckboxFilters] = useState(
    loadInitialState().checkboxFilters
  );
  const [tempCheckboxFilters, setTempCheckboxFilters] = useState(
    loadInitialState().checkboxFilters
  );
  const [columnSearchTerms, setColumnSearchTerms] = useState(
    loadInitialState().columnSearchTerms
  );
  // Track empty values filter per column
  const [includeEmptyValues, setIncludeEmptyValues] = useState<any>({});

  const [showUnassigned, setShowUnassigned] = useState<any>(
    loadInitialState().showUnassigned
  );

  const [columnsWithNulls, setColumnsWithNulls] = useState<
    Record<string, boolean>
  >({});

  const [error, setError] = useState<any>(false);
  const [errorMessage, setErrorMessage] = useState<any>("");

  const [success, setSuccess] = useState<any>(false);
  const [successMessage, setSuccessMessage] = useState<any>("");

  // Save state to localStorage whenever it changes
  React.useEffect(() => {
    const stateToSave: TableState = {
      orderBy,
      order,
      page,
      rowsPerPage,
      filters,
      groupBy,
      globalSearch: searchState.globalSearch,
      columnFilters: searchState.columnFilters,
      checkboxFilters,
      columnSearchTerms,
      showUnassigned,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  }, [
    orderBy,
    order,
    page,
    rowsPerPage,
    filters,
    groupBy,
    searchState,
    checkboxFilters,
    columnSearchTerms,
    showUnassigned,
  ]);

  const getUniqueValues = (columnKey: any) => {
    const uniqueSet = new Set();
    data.forEach((row) => {
      if (
        columnKey === "parentAccountDetails" &&
        Array.isArray(row[columnKey])
      ) {
        // Extract names from parentAccountDetails array
        row[columnKey]?.forEach((account) => {
          if (account?.name) {
            uniqueSet.add(account.name);
          }
        });
      } else if (columnKey === "tapClicksId") {
        if (row[columnKey] !== null && row[columnKey] !== undefined) {
          uniqueSet.add(parseInt(row[columnKey]));
        }
      } else if (
        columnKey === "lastUpdatedDate" ||
        columnKey === "createdDate"
      ) {
        // For date fields, standardize the format before adding to set
        if (row[columnKey] !== null && row[columnKey] !== undefined) {
          const date = new Date(row[columnKey]);
          if (!isNaN(date.getTime())) {
            // Convert to consistent format
            const standardDate = date.toLocaleDateString();
            uniqueSet.add(standardDate);
          }
        }
      } else if (columnKey === "systemCreatedDateTime") {
        if (row[columnKey] !== null && row[columnKey] !== undefined) {
          const date = new Date(row[columnKey]);
          if (!isNaN(date.getTime())) {
            // Convert to consistent format
            const standardDate = date.toLocaleDateString();
            uniqueSet.add(standardDate);
          }
        }
      } else if (columnKey === "isSensitive") {
        if (row[columnKey] !== null && row[columnKey] !== undefined) {
          uniqueSet.add("Non-Sensitive");
          uniqueSet.add("Sensitive");
        }
      } else if (columnKey === "status") {
        if (row[columnKey] !== null && row[columnKey] !== undefined) {
          uniqueSet.add("Active");
          uniqueSet.add("In Active");
        }
      } else {
        // Handle regular columns
        if (row[columnKey] !== null && row[columnKey] !== undefined) {
          uniqueSet.add(row[columnKey]);
        }
      }
    });
    // Convert set to array and sort
    const sortedArray = Array.from(uniqueSet);
    // Sort based on column type
    if (
      columnKey === "lastUpdatedDate" ||
      columnKey === "createdDate" ||
      columnKey === "systemCreatedDateTime"
    ) {
      return sortedArray.sort(
        (a: any, b: any) => new Date(a).getTime() - new Date(b).getTime()
      );
    } else if (columnKey === "tapClicksId") {
      return sortedArray.sort((a: any, b: any) => a - b);
    } else {
      return sortedArray.sort();
    }
  };

  const filteredOptions = (columnKey: string) => {
    const searchTerm = columnSearchTerms[columnKey] || "";

    if (!activeFilter) return [];

    const options = getUniqueValues(activeFilter);

    const filteredValues = options.filter((value: any) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort values into checked and unchecked arrays
    const checkedValues = filteredValues.filter(
      (value: any) => tempCheckboxFilters[columnKey]?.[value]
    );
    const uncheckedValues = filteredValues.filter(
      (value: any) => !tempCheckboxFilters[columnKey]?.[value]
    );

    // Sort each array alphabetically
    checkedValues.sort((a: any, b: any) => {
      // Special handling for dates
      if (
        columnKey === "lastUpdatedDate" ||
        columnKey === "createdDate" ||
        columnKey === "systemCreatedDateTime"
      ) {
        return new Date(a).getTime() - new Date(b).getTime();
      }
      // Special handling for client status
      if (columnKey === "clientStatus") {
        return CLIENT_STATUS[a].localeCompare(CLIENT_STATUS[b]);
      }

      if (columnKey === "tapClicksId") {
        const aNum = parseInt(a) || 0;
        const bNum = parseInt(b) || 0;
        return aNum - bNum;
      }
      // Default string comparison
      return a.localeCompare(b);
    });

    uncheckedValues.sort((a: any, b: any) => {
      // Special handling for dates
      if (
        columnKey === "lastUpdatedDate" ||
        columnKey === "createdDate" ||
        columnKey === "systemCreatedDateTime"
      ) {
        return new Date(a).getTime() - new Date(b).getTime();
      }
      // Special handling for client status
      if (columnKey === "clientStatus") {
        return CLIENT_STATUS[a].localeCompare(CLIENT_STATUS[b]);
      }
      if (columnKey === "tapClicksId") {
        const aNum = parseInt(a) || 0;
        const bNum = parseInt(b) || 0;
        return aNum - bNum;
      }
      // Default string comparison
      return a.localeCompare(b);
    });

    // Combine arrays with checked values first
    return [...checkedValues, ...uncheckedValues];
  };

  // Handle temporary checkbox filter changes
  const handleTempCheckboxFilterChange = (
    column: string,
    value: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTempCheckboxFilters((prev: any) => ({
      ...prev,
      [column]: {
        ...(prev[column] || {}),
        [value]: event.target.checked,
      },
    }));
  };

  // Handle column search input changes
  const handleColumnSearchChange = (column: string, value: string) => {
    setColumnSearchTerms((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  // Handle filter submission
  const handleFilterSubmit = (column: string) => {
    setCheckboxFilters((prev: any) => ({
      ...prev,
      [column]: tempCheckboxFilters[column] || {},
    }));
    setColumnSearchTerms((prev: any) => ({
      ...prev,
      [column]: "",
    }));
    setFilterMenuAnchor(null);
  };

  const columnHasNulls = (columnKey: string): boolean => {
    return columnsWithNulls[columnKey] || false;
  };

  // Updated memoized sorting function with proper type handling
  const sortFunction = useMemo(() => {
    return (a: any, b: any) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Special handling for tapClicksId (numeric comparison)
      if (orderBy === "tapClicksId") {
        const aNum = parseInt(aValue) || 0;
        const bNum = parseInt(bValue) || 0;
        return order === "desc" ? bNum - aNum : aNum - bNum;
      }

      // Handle numeric values
      if (!isNaN(aValue) && !isNaN(bValue)) {
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        return order === "desc" ? bNum - aNum : aNum - bNum;
      }

      // Handle dates
      if (isValidDate(aValue) && isValidDate(bValue)) {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return order === "desc"
          ? bDate.getTime() - aDate.getTime()
          : aDate.getTime() - bDate.getTime();
      }

      // Default string comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return order === "desc"
        ? bStr.localeCompare(aStr)
        : aStr.localeCompare(bStr);
    };
  }, [orderBy, order]);

  // Helper function to check if a value is a valid date
  const isValidDate = (value: any): boolean => {
    const date = new Date(value);
    return date instanceof Date && !isNaN(date.getTime());
  };

  // Memoized Column filtering
  const filteredColumnData = useMemo(() => {
    let result = data ? [...data] : [];
    // Apply checkbox filters for each column
    Object.keys(checkboxFilters).forEach((key) => {
      const selectedValues = Object.entries(checkboxFilters[key] || {})
        .filter(([_, checked]) => checked)
        .map(([value]) => value);

      if (selectedValues?.length > 0 || includeEmptyValues[key]) {
        result = result?.filter((item) => {
          // Handle empty values if that filter is checked
          if (
            includeEmptyValues[key] &&
            (item[key] === undefined ||
              item[key] === null ||
              item[key] === "" ||
              (Array.isArray(item[key]) && item[key].length === 0))
          ) {
            return true;
          }

          // Convert date objects to formatted strings
          if (
            key === "createdDate" ||
            key === "lastUpdatedDate" ||
            key === "systemCreatedDateTime"
          ) {
            const date = new Date(item[key]);
            if (!isNaN(date.getTime())) {
              let stringValue;

              // Special handling for systemCreatedDateTime to include time
              if (false) {
                const month = (date.getMonth() + 1).toString().padStart(2, "0");
                const day = date.getDate().toString().padStart(2, "0");
                const year = date.getFullYear().toString().slice(-2);
                const hours = date.getHours().toString().padStart(2, "0");
                const minutes = date.getMinutes().toString().padStart(2, "0");

                stringValue = `${month}/${day}/${year} - ${hours}:${minutes}`;
              } else {
                // Original format for other date fields
                stringValue = `${
                  date.getMonth() + 1
                }/${date.getDate()}/${date.getFullYear()}`;
              }

              return selectedValues.some(
                (val) => String(val).toLowerCase() === stringValue.toLowerCase()
              );
            }
            return false;
          }

          if (key === "parentAccountDetails") {
            if (!item[key]) return selectedValues.length === 0;
            return item[key].some((account: any) =>
              selectedValues.includes(account.name)
            );
          }

          if (key === "isSensitive") {
            if (item[key] === null || item[key] === undefined) {
              return false;
            }

            if (
              selectedValues.includes("Non-Sensitive") &&
              selectedValues.includes("Sensitive")
            ) {
              return true;
            } else if (selectedValues.includes("Non-Sensitive")) {
              return item[key] === false;
            } else if (selectedValues.includes("Sensitive")) {
              return item[key] === true;
            }
            return false;
          }

          if (key === "status") {
            if (item[key] === null || item[key] === undefined) {
              return false;
            }

            if (
              selectedValues.includes("In Active") &&
              selectedValues.includes("Active")
            ) {
              return true;
            } else if (selectedValues.includes("In Active")) {
              return item[key] === false;
            } else if (selectedValues.includes("Active")) {
              return item[key] === true;
            }
            return false;
          }

          // For other columns, directly check if the value is included in selectedValues
          return selectedValues.includes(String(item[key]));
        });
      }
    });
    return result;
  }, [data, checkboxFilters, includeEmptyValues]);

  React.useEffect(() => {
    // Get all column keys from your data schema
    const allColumnKeys = data && Object.keys(data[0] || {});

    // Initialize the columnsWithNulls state
    const initialNullState = allColumnKeys?.reduce((acc, columnKey) => {
      const hasNulls = data.some(
        (row) =>
          row[columnKey] === null ||
          row[columnKey] === undefined ||
          row[columnKey]?.length == 0
      );
      return { ...acc, [columnKey]: hasNulls };
    }, {});

    setColumnsWithNulls(initialNullState);
  }, [data]);

  // modal popup states
  const [openFileUpload, setOpenFileUpload] = React.useState(false);
  const [fileUploadType, setFileUploadType] = React.useState("");

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleUploadClick = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleUploadClose = () => {
    setAnchorEl(null);
  };

  const [anchorE2, setAnchorE2] = React.useState<null | HTMLElement>(null);
  const openBulkMenu = Boolean(anchorE2);

  const handleBulkClick = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorE2) {
      setAnchorE2(null);
    } else {
      setAnchorE2(event.currentTarget);
    }
  };

  const handleBulkClose = () => {
    setAnchorE2(null);
  };

  React.useEffect(() => {
    if (isEditMode && editData) {
      setOpenFileUpload(isEditMode);
      setFileUploadType(
        editData?.uploadTypeId == 1 ? "fileUpload" : "research"
      );
    }
  }, [isEditMode, editData]);

  const handleClickFileUpload = (type: any) => {
    setOpenFileUpload(true);
    setFileUploadType(type);
  };

  const handlePopUpClose = () => {
    setOpenFileUpload(false);
    setFileUploadType("");
    setAnchorEl(null);
    handleFilePopUpClose();
  };

  // Memoized filtering function
  const filteredData = useMemo(() => {
    let result = filteredColumnData;

    console.log("showUnassigned", showUnassigned);

    if (showUnassigned) {
      result = result?.filter((item) => item?.isNewClient === true);
    }

    return result.filter((item) => {
      // Parse dates properly
      const itemWithParsedDates = {
        ...item,
        createdDate:
          item.createdDate instanceof Date
            ? item.createdDate
            : new Date(item.createdDate),
        lastUpdatedDate:
          item.lastUpdatedDate instanceof Date
            ? item.lastUpdatedDate
            : new Date(item.lastUpdatedDate),
        systemCreatedDateTime:
          item.systemCreatedDateTime instanceof Date
            ? item.systemCreatedDateTime
            : new Date(item.systemCreatedDateTime),
      };

      if (searchState.globalSearch) {
        const searchTerm = searchState.globalSearch.toLowerCase().trim();

        if ("Sensitive".toLowerCase().includes(searchTerm.toLowerCase())) {
          return item.isSensitive === true;
        } else if (
          "Non-Sensitive".toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          return item.isSensitive === false;
        }

        return Object.entries(itemWithParsedDates).some(([key, value]) => {
          if (key === "parentAccount") return false;
          // Handle numeric values
          const numericSearchTerm = parseFloat(searchTerm);
          const isNumericSearch = !isNaN(numericSearchTerm);

          if (searchTerm.includes("/")) {
            if (value instanceof Date && !isNaN(value.getTime())) {
              const formattedDate = `${
                value.getMonth() + 1
              }/${value.getDate()}/${value.getFullYear()}`;
              if (
                formattedDate.toLowerCase().includes(searchTerm.toLowerCase())
              ) {
                return true;
              }
            }
          }

          if (
            isNumericSearch &&
            typeof value === "number" &&
            !searchTerm.includes("/")
          ) {
            return (
              value === numericSearchTerm && value.toString() == searchTerm
            );
          }

          // Handle string values
          return (
            !Array.isArray(value) &&
            typeof value !== "undefined" &&
            value !== null &&
            String(value).toLowerCase().includes(searchTerm.toLowerCase())
          );
        });
      }

      // Column filters remain the same
      return Object.entries(searchState.columnFilters).every(([key, value]) => {
        if (!value) return true;
        const itemValue =
          item[key as keyof any]?.toString().toLowerCase() || "";

        return itemValue.includes(value?.toLowerCase());
      });
    });
  }, [filteredColumnData, searchState, showUnassigned]);

  // Memoized grouped data
  const processedData = useMemo(() => {
    if (!groupBy) {
      return {
        sortedData: [...filteredData].sort(sortFunction),
        totalCount: filteredData.length,
      };
    }

    const grouped: GroupedData = {};
    const sorted = [...filteredData].sort(sortFunction);

    sorted.forEach((item) => {
      const groupValue = item[groupBy]?.toString() || "Unknown";
      if (!grouped[groupValue]) {
        grouped[groupValue] = {
          items: [],
          isExpanded: true, // Default expanded
        };
      }
      grouped[groupValue].items.push(item);
    });

    setGroupedData(grouped);

    // Calculate total visible rows for pagination
    const totalVisible = Object.entries(grouped).reduce((acc, [_, group]) => {
      return acc + (group.isExpanded ? group.items.length : 0);
    }, 0);

    return {
      sortedData: sorted,
      totalCount: totalVisible,
    };
  }, [filteredData, sortFunction, groupBy]);

  // Pagination calculation
  const paginatedData = useMemo(() => {
    if (!groupBy) {
      return processedData.sortedData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );
    }

    let currentCount = 0;
    let visibleRows: any[] = [];
    let skipCount = page * rowsPerPage;
    let targetCount = rowsPerPage;

    Object.entries(groupedData).forEach(([groupValue, group]) => {
      if (group.isExpanded) {
        if (skipCount > 0) {
          const toSkip = Math.min(skipCount, group.items.length);
          skipCount -= toSkip;
          currentCount += toSkip;
        } else if (visibleRows.length < targetCount) {
          const remaining = targetCount - visibleRows.length;
          const toAdd = group.items.slice(0, remaining);
          visibleRows = visibleRows.concat(toAdd);
        }
      }
    });

    return visibleRows;
  }, [processedData.sortedData, page, rowsPerPage, groupBy, groupedData]);

  // Handlers
  const handleRequestSort = (property: keyof any) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleGlobalSearch = (value: string) => {
    setSearchState((prev) => ({
      ...prev,
      globalSearch: value,
    }));
    setPage(0); // Reset to first page when searching
  };

  const handleGroupChange = (event: SelectChangeEvent) => {
    setGroupBy(event.target.value as keyof any | "");
    setPage(0);
  };

  const toggleGroup = (groupValue: string) => {
    setGroupedData((prev) => ({
      ...prev,
      [groupValue]: {
        ...prev[groupValue],
        isExpanded: !prev[groupValue].isExpanded,
      },
    }));
  };

  const getParentValues = (value: any) => {
    let parent = `"${value?.map((item: any) => item?.name).join(", ")}"`;
    return parent;
  };

  const exportToCSV = () => {
    // Sort the data before export using the current sort settings
    const sortedData = [...filteredData].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Special handling for tapClicksId (numeric comparison)
      if (orderBy === "tapClicksId") {
        const aNum = parseInt(aValue) || 0;
        const bNum = parseInt(bValue) || 0;
        return order === "desc" ? bNum - aNum : aNum - bNum;
      }

      // Handle numeric values
      if (!isNaN(aValue) && !isNaN(bValue)) {
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        return order === "desc" ? bNum - aNum : aNum - bNum;
      }

      // Handle dates
      if (isValidDate(aValue) && isValidDate(bValue)) {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return order === "desc"
          ? bDate.getTime() - aDate.getTime()
          : aDate.getTime() - bDate.getTime();
      }

      // Default string comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return order === "desc"
        ? bStr.localeCompare(aStr)
        : aStr.localeCompare(bStr);
    });

    // Create the CSV data with proper escaping for CSV format
    const headers = columns.map((cell) => `"${cell.label}"`).join(",");
    const rows = sortedData
      .map((item) =>
        columns
          .map((cell) => {
            let value;
            if (Array.isArray(item[cell.value])) {
              value = getParentValues(item[cell.value]);
            } else if (
              cell.value == "createdDate" ||
              cell.value == "lastUpdatedDate"
            ) {
              value = `"${new Date(item[cell.value]).toLocaleDateString()}"`;
            } else if (cell.value == "status") {
              value = item[cell.value] ? "Active" : "In Active";
            } else if (cell.value == "systemCreatedDateTime") {
              value = `"${new Date(item[cell.value]).toLocaleDateString()}"`;
            } else if (cell.value == "clientStatus") {
              value = `"${CLIENT_STATUS[item[cell.value]]}"`;
            } else {
              // Properly escape double quotes for CSV format
              value =
                item[cell.value] !== null && item[cell.value] !== undefined
                  ? `"${String(item[cell.value]).replace(/"/g, '""')}"`
                  : '""';
            }
            return value;
          })
          .join(",")
      )
      .join("\n");

    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${exportFileName}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleEmptyValuesChange = (column: string, checked: boolean) => {
    setIncludeEmptyValues((prev: any) => ({
      ...prev,
      [column]: checked,
    }));
  };

  const hasActiveFilter = (column: string) => {
    if (includeEmptyValues[column]) return true;

    const selectedValues = Object.entries(checkboxFilters[column] || {})
      .filter(([_, checked]) => checked)
      .map(([value]) => value);

    return selectedValues.length > 0;
  };

  const clearPersistedState = () => {
    localStorage.removeItem(STORAGE_KEY);
    // Reset all states to default values
    setOrderBy(defaultSortColumn);
    setOrder(defaultSortDirection);
    setPage(0);
    setRowsPerPage(rowsPerPageOptions[0]);
    setFilters({});
    setGroupBy("");
    setSearchState({
      globalSearch: "",
      columnFilters: {},
    });
    setCheckboxFilters({});
    setColumnSearchTerms({});
    setIncludeEmptyValues({});
    setTempCheckboxFilters({});
    setShowUnassigned(false);
  };

  // Example usage:

  // Render function JSX

  const renderActionIcons = (cell: any, row: any) => {
    return (
      <>
        <Box className="btnActionColumn d-flex justify-content-end">
          {row?.uploadTypeId == 2 &&
            !getUserRoles().includes("External User") && (
              <IconButton
                aria-label="edit"
                size="medium"
                className="iconBtn"
                onClick={(e) => {
                  e.stopPropagation();
                  handelAction(row, "Edit", null);
                }}
              >
                <img src={EditIconSvg} alt="edit Icon" />
              </IconButton>
            )}

          {row?.filePath?.length > 0 && (
            <IconButton
              aria-label="download"
              size="medium"
              onClick={(e) => {
                e.stopPropagation();
                handelAction(row, "Download", null);
              }}
              className="iconBtn"
            >
              <img src={DownloadIconSvg} alt="Download Icon" />
            </IconButton>
          )}

          {(
            <IconButton
              aria-label="delete"
              size="medium"
              disabled={
                !(
                  getUserRoles().includes("Admin") ||
                  getUserRoles().includes("Administrator") ||
                  window.localStorage.getItem("USER_ID") == row?.createdById
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                handelAction(row, "Delete", null);
              }}
              className="iconBtn"
            >
              <img src={DeleteIconSvg} alt="Delete Icon" />
            </IconButton>
          )}
        </Box>
      </>
    );
  };

  const renderClientStatus = (cell: any, row: any) => {
    return row[cell?.value] ? (
      <Chip
        label={CLIENT_STATUS[row[cell?.value]]}
        size="small"
        variant="filled"
        className={CLIENT_STATUS[row[cell?.value]]?.toLowerCase()}
      />
    ) : (
      <Box sx={{ textAlign: "center" }}>-</Box>
    );
  };

  const renderClientName = (cell: any, row: any) => {
    return row[cell?.value] && row?.isNewClient ? (
      <>
        <Tooltip arrow title={row[cell?.value]}>
          <Box className="recentDot" component="span"></Box>
          {row[cell?.value]?.length > 30
            ? row[cell?.value].substring(0, 30) + "..."
            : row[cell?.value]}
        </Tooltip>
      </>
    ) : (
      <>
        <Tooltip arrow title={row[cell?.value]}>
          {row[cell?.value]?.length > 30
            ? row[cell?.value].substring(0, 30) + "..."
            : row[cell?.value]}
        </Tooltip>
      </>
    );
  };

  const renderConnection = (cell: any, row: any) => {
    let name = row[cell?.value];
    return (
      <>
        <Tooltip arrow title={name}>
          {name?.length > 30 ? name.substring(0, 30) + "..." : name}
        </Tooltip>
      </>
    );
  };

  const handleDownload = (row: any, cell: any) => {
    if (row[cell?.value]?.length === 1) {
      handelAction(row, "Download", null);
    } else {
      handleAttachmentsDownload(row);
    }
  };

  const renderResearchExpAttachments = (cell: any, row: any) => {
    const filename =
      row?.filePath &&
      row?.filePath?.length > 0 &&
      row.filePath[0].split("/").pop().split("?")[0];
    return (
      filename && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            handleDownload(row, cell);
          }}
        >
          {/* <IconButton
            aria-label="download"
            size="medium"
            className="iconBtn"
            sx={{ mr: 0.5 }}
          >
            <img src={DownloadIconSvg} alt="Download Icon" />
          </IconButton> */}

          <Tooltip arrow title={row[cell?.value]?.length == 1 ? filename : ""}>
            <Box
              component="span"
              sx={{
                textDecoration: "underline",
                p: "0 !important",
                cursor: "pointer",
              }}
            >
              {row[cell?.value]?.length == 1
                ? filename?.length > 26
                  ? filename?.substring(0, 26) + "..."
                  : filename
                : `${row[cell?.value]?.length} files`}
            </Box>
          </Tooltip>
        </div>
      )
    );
  };

  const renderParentAccount = (cell: any, row: any) => {
    if (Array.isArray(row[cell.value]) && row[cell.value].length > 0) {
      let parentAccount: any =
        row[cell?.value]
          ?.map((item: any) => item?.name)
          .join(", ")
          .substring(0, 35) + "...";
      return parentAccount ? (
        <Tooltip
          arrow
          title={row[cell?.value]?.map((item: any) => item?.name).join(", ")}
        >
          {parentAccount}
        </Tooltip>
      ) : (
        <Box sx={{ textAlign: "center" }}>-</Box>
      );
    }
    return <Box sx={{ textAlign: "center" }}>-</Box>;
  };

  const renderFileName = (cell: any, row: any) => {
    let name = row?.fileName;
    return (
      <>
        <Tooltip arrow title={name}>
          {name?.length > 30 ? name.substring(0, 30) + "..." : name}
        </Tooltip>
      </>
    );
  };

  const renderPlatformStatusIcons = (cell: any, row: any) => {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          handelAction(row, "PlatformStatus", row?.status);
        }}
      >
        <Switch
          {...label}
          checked={row?.status}
          className="platformStatusSwitch"
        />
        {row[cell?.value]}
      </div>
    );
  };

  const renderFileClassifiedIcons = (cell: any, row: any) => {
    return (
      <>
        {row.uploadTypeId == 1 &&
          (row[cell.value] ? (
            <IconButton
              aria-label="Sensitive"
              size="small"
              className="icon-sensitive"
              onClick={(e) => {
                e.stopPropagation();
                handelAction(row, "Status", false);
              }}
            >
              <img src={SensitiveIconSvg} alt="Sensitive Icon" />
            </IconButton>
          ) : (
            <IconButton
              aria-label="Non Sensitive"
              size="small"
              className="icon-sensitive"
              onClick={(e) => {
                e.stopPropagation();
                handelAction(row, "Status", true);
              }}
            >
              <img src={NonSensitiveIconSvg} alt="Non Sensitive Icon" />
            </IconButton>
          ))}
        {row.uploadTypeId == 1 &&
          (row[cell.value] ? "Sensitive" : "Non-Sensitive")}
      </>
    );
  };

  const getFixedColumnStyle = (index: number, isHeader: boolean = false) => {
    if (!isFixedColumn) return {};

    // For right fixed column, we want index 0 (first column)
    // For left fixed column, we want the last visible column
    const visibleColumns = columns.filter((col) => col.showColumn);
    const lastVisibleIndex = visibleColumns.length - 1;

    // Determine if this is our target column to fix
    // file list - file classified column th style
    const isTargetColumn = isFixedRightColumn
      ? index === 0 // First column (left side)
      : index === lastVisibleIndex; // Last column (right side)

    if (!isTargetColumn) return {};

    return {
      position: "sticky",
      // If fixing right column, it should be on left: 0
      // If fixing left column, it should be on right: 0
      [isFixedRightColumn ? "left" : "right"]: 0,
      backgroundColor: isHeader ? "white" : "white",
      zIndex: isHeader ? 3 : 2,
      // boxShadow: isFixedRightColumn
      //   ? "2px 0 4px rgba(0,0,0,0.1)"
      //   : "-2px 0 4px rgba(0,0,0,0.1)",
      // "&::after": {
      //   content: '""',
      //   position: "absolute",
      //   top: 0,
      //   bottom: 0,
      //   [isFixedRightColumn ? "right" : "left"]: 0,
      //   width: "1px",
      //   backgroundColor: "rgba(0,0,0,0.12)",
      // },
    };
  };

  const renderFileListDates = (row: any, cell: any) => {
    let date = row["updatedDateTime"]
      ? row["updatedDateTime"]
      : row["systemCreatedDateTime"];
    return new Date(date).toLocaleDateString();
  };

  const renderTableCells = (row: any, isHeader: boolean = false) => {
    return columns.map((cell, index) => {
      const isFirstColumn = index === 0;
      const isLastColumn = index === columns.length - 1;
      const shouldUseFixedCell =
        isFixedColumn &&
        ((isFixedRightColumn && isFirstColumn) ||
          (!isFixedRightColumn && isLastColumn));

      const CellComponent = shouldUseFixedCell ? FixedTableCell : TableCell;
      const fixedStyle = getFixedColumnStyle(index, isHeader);

      if (isHeader && cell?.showColumn) {
        return (
          <CellComponent
            key={cell.value}
            sortDirection={orderBy === cell.value ? order : false}
            sx={{
              width: isLastColumn ? lastColumnWidth : remainingColumnsWidth,
              ...fixedStyle,
              backgroundColor: "white",
              fontWeight: "bold",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {cell.label}

              {cell.showFilter && (
                <Tooltip title="Filter" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setActiveFilter(cell.value);
                      setFilterMenuAnchor(e.currentTarget);
                    }}
                  >
                    {!hasActiveFilter(cell.value) && (
                      <img src={FilterIconSvg} alt="Filter Icon" />
                    )}
                    {hasActiveFilter(cell.value) && (
                      <img src={FilterSelectIconSvg} alt="Filter Icon" />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              {cell.showFilter && (
                <TableSortLabel
                  active={orderBy === cell.value}
                  direction={orderBy === cell.value ? order : "asc"}
                  onClick={() => handleRequestSort(cell.value)}
                />
              )}
            </Box>
          </CellComponent>
        );
      }

      if (cell?.showColumn) {
        return (
          <CellComponent
            key={cell?.value}
            sx={{
              width: isLastColumn ? lastColumnWidth : remainingColumnsWidth,
              ...fixedStyle,
            }}
            onClick={(e) => {
              // Prevent row click event if the action column is clicked
              if (cell?.value !== "status") {
                onRowClick(row);
              }
            }}
          >
            {cell?.value == "parentAccountDetails" &&
              renderParentAccount(cell, row)}
            {cell?.value == "filePath" &&
              renderResearchExpAttachments(cell, row)}

            {cell?.value == "clientStatus" && renderClientStatus(cell, row)}
            {cell?.value == "clientName" && renderClientName(cell, row)}
            {cell?.value == "connection" && renderConnection(cell, row)}
            {cell?.value == "action" && renderActionIcons(cell, row)}
            {cell?.value == "status" && renderPlatformStatusIcons(cell, row)}
            {cell?.value == "isSensitive" &&
              renderFileClassifiedIcons(cell, row)}

            {cell?.value == "fileName" && renderFileName(cell, row)}

            {cell?.value != "action" &&
              cell?.value != "status" &&
              cell?.value != "isSensitive" &&
              cell?.value != "clientStatus" &&
              cell?.value != "parentAccountDetails" &&
              cell?.value != "clientName" &&
              cell?.value != "fileName" &&
              cell?.value != "filePath" &&
              cell?.value != "connection" &&
              (cell?.date ? (
                cell.value == "systemCreatedDateTime" ? (
                  new Date(row[cell.value]).toLocaleDateString()
                ) : (
                  new Date(row[cell.value]).toLocaleDateString()
                )
              ) : row[cell?.value] ? (
                row[cell?.value]
              ) : (
                <Box sx={{ textAlign: "center" }}>-</Box>
              ))}
          </CellComponent>
        );
      }
    });
  };

  const renderGroupTableCells = (row: any, groupLength: any, index: any) => {
    return columns.map((cell, cellIndex) => {
      return (
        <TableCell
          key={cell.value}
          style={{
            width:
              cellIndex === columns.length - 1
                ? lastColumnWidth
                : remainingColumnsWidth,
            borderBottom: index === groupLength - 1 ? "none" : undefined,
            ...(cellIndex === columns.length - 1 && {
              position: "sticky",
              right: 0,
              backgroundColor: "white",
              zIndex: 1,
              borderLeft: "solid 1px rgba(0,0,0,0.12)",
              // boxShadow: "-2px 0 4px rgba(0,0,0,0.1)",
            }),
          }}
        >
          {cell?.date
            ? new Date(row[cell.value]).toLocaleDateString()
            : row[cell.value]}
        </TableCell>
      );
    });
  };

  const renderTableBody = () => {
    if (!groupBy) {
      return paginatedData && paginatedData?.length > 0 ? (
        paginatedData?.map((row, index) => (
          <TableRow key={index}>{renderTableCells(row)}</TableRow>
        ))
      ) : (
        <TableRow className="bg-white noRecords">
          <TableCell className="bg-white" colSpan={10}>
            <Alert
              severity="warning"
              sx={{ mt: 0.5, mb: 0.6, display: "inline-flex" }}
            >
              No records found.
            </Alert>
          </TableCell>
        </TableRow>
      );
    }

    return Object.entries(groupedData).map(([groupValue, group]) => (
      <React.Fragment key={groupValue}>
        <TableRow
          className="bg-gray-100 cursor-pointer"
          onClick={() => toggleGroup(groupValue)}
        >
          <TableCell colSpan={columns?.length}>
            <Box className="flex items-center gap-2">
              {group?.isExpanded ? <ExpandIcon /> : <CollapseIcon />}
              <strong>{`${String(groupBy)}: ${groupValue} (${
                group?.items?.length
              } items)`}</strong>
            </Box>
          </TableCell>
          <FixedTableCell /> {/* Empty fixed cell for group header */}
        </TableRow>
        <TableRow>
          <TableCell colSpan={columns?.length} className="p-0">
            <Collapse in={group?.isExpanded}>
              <Table>
                <TableBody>
                  {group?.items?.map((row, index) => (
                    <TableRow key={index}>
                      {renderGroupTableCells(row, group?.items?.length, index)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    ));
  };

  const handleSave = (fileName: any) => {
    console.log("handleSave original grid", fileUploadType);
    const message = isEditMode
      ? "has been updated."
      : fileUploadType == "research"
      ? "submitted."
      : "uploaded.";
    const fileSuccessText = `${fileName} ${message}`;
    setOpenFileUpload(false);
    setFileUploadType("");
    setAnchorEl(null);
    setSuccess(true);
    setSuccessMessage(fileSuccessText);
    handleSaveFormSubmission();
    queryClient.invalidateQueries({ queryKey: ["files"] });
    if (fileUploadType == "research") {
      queryClient.invalidateQueries({ queryKey: ["experiments"] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["files"] });
    }
  };

  const hideSuccess = () => {
    setSuccess(false);
    setSuccessMessage("");
  };

  const hideError = () => {
    setError(false);
  };

  const clearGlobalSearch = () => {
    setSearchState((prev) => ({
      ...prev,
      globalSearch: "",
    }));
  };

  return (
    <TableListStyled>
      <Box className="tableColumn">
        <Box className="d-flex align-items-center justify-content-start">
          <Box>
            <Box className="searchWrapper">
              <TextField
                size="small"
                label="Search"
                placeholder={placeholder}
                value={searchState.globalSearch}
                onChange={(e) => handleGlobalSearch(e.target.value)}
                className="customTableInputSearch"
                variant="standard"
                InputProps={{
                  type: "search",
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <div className="iconGlobalClear" onClick={clearGlobalSearch}>
                <CloseIcon />
              </div>
            </Box>
            {isGroupingEnabled && (
              <FormControl
                size="small"
                variant="standard"
                className="customTableGroup"
              >
                <InputLabel>Group By</InputLabel>
                <Select
                  value={String(groupBy)}
                  label="Group By"
                  onChange={handleGroupChange}
                  style={{ minWidth: "120px" }}
                >
                  <MenuItem value="">None</MenuItem>
                  {columns.map((cell) => (
                    <MenuItem key={cell.value} value={cell.value}>
                      {cell.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {showUnassignedToggle && (
            <Box className="d-flex align-items-center">
              <Switch
                {...label}
                checked={showUnassigned}
                onChange={(e) => {
                  setShowUnassigned(e.target.checked);
                  setPage(0);
                }}
                className="assignStatusSwitch"
              />
              <Typography
                variant="body1"
                sx={{ color: colors.white }}
                className="fs-13 lblAssignClients"
              >
                Show unassigned clients
              </Typography>
            </Box>
          )}
        </Box>
        <Box className="d-flex">
          {/* <>
            <Button
              id="bulk-action-button"
              aria-controls={openBulkMenu ? "Bulk action menu" : undefined}
              aria-haspopup="true"
              aria-expanded={openBulkMenu ? "true" : undefined}
              variant="outlined"
              disableElevation
              onClick={handleBulkClick}
              endIcon={<ArrowDropDownIcon />}            
              className="btn-clearfilter btn-bulk-action "
            >
              Bulk Actions
            </Button>

            <StyledMenu
                id="bulk-action-button"
                MenuListProps={{
                  "aria-labelledby": "Bulk action menu",
                }}
                anchorEl={anchorE2}
                open={openBulkMenu}
                onClose={(event, reason) => {
                  handleBulkClose();  
                }}
              >
                <MenuItem
                  disableRipple
                  onClick={() => {
                    handleBulkClose();
                  }}
                >
                  <ListItemIcon>      
                    <img src={NonSensitiveIconSvg} alt="Non Sensitive Icon" />
                  </ListItemIcon>
                  <Typography variant="body1"> 
                    Mark as sensitive
                  </Typography>
                </MenuItem>             
                <MenuItem
                  onClick={() => {                     
                    handleBulkClose();
                  }}
                  disableRipple
                >
                  <ListItemIcon>                  
                    <img src={SensitiveIconGraySvg} alt="Sensitive Icon" />
                  </ListItemIcon>
                  <Typography variant="body1"> 
                    Mark as non-sensitive
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {                     
                    handleBulkClose();
                  }}
                  disableRipple
                >
                  <ListItemIcon>                  
                    <img src={DownloadIconSvg} alt="Download Icon" />
                  </ListItemIcon>
                  <Typography variant="body1"> 
                    Download
                  </Typography>  
                </MenuItem>
                <MenuItem
                  onClick={() => {                     
                    handleBulkClose();
                  }}
                  disableRipple
                >
                  <ListItemIcon>                  
                    <img src={DeleteIconSvg} alt="Delete Icon" />
                  </ListItemIcon>
                  <Typography variant="body1"> 
                    Delete
                  </Typography>
                </MenuItem>
               
              </StyledMenu>
          </> */}

          <Tooltip title="Clear all filters" arrow>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              className="btn-clearfilter"
              onClick={clearPersistedState}
            >
              Clear filters
            </Button>
          </Tooltip>

          {isExportEnabled && (
            <Tooltip title="Export to CSV" arrow>
              <Button
                variant="contained"
                onClick={exportToCSV}
                sx={{ ml: "10px" }}
                className="btn-export-table-data"
              >
                Export Table Data
              </Button>
            </Tooltip>
          )}

          {getUserRoles().includes("Administrator") ||
          getUserRoles().includes("Internal User") ? (
            <>
              {showFileUploadModal && (
                <Button
                  id="upload-file-button"
                  aria-controls={open ? "Upload file menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  variant="contained"
                  disableElevation
                  onClick={handleUploadClick}
                  endIcon={<ArrowDropDownIcon />}
                  startIcon={<UploadFileIcon />}
                  className="btn-upload-file"
                >
                  Uploads
                </Button>
              )}

              <StyledMenu
                id="upload-file-button"
                MenuListProps={{
                  "aria-labelledby": "Upload file menu",
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={(event, reason) => {
                  // Prevent closing when clicking outside or pressing Escape

                  handleUploadClose(); // Close the menu only when explicitly called
                }}
              >
                <MenuItem
                  disableRipple
                  onClick={() => {
                    handleClickFileUpload("fileUpload");
                    handleUploadClose();
                  }}
                >
                  File Upload
                </MenuItem>
                {(getUserRoles().includes("Administrator") ||
                  getUserRoles().includes("Internal User")) && (
                  <MenuItem
                    onClick={() => {
                      handleClickFileUpload("research");
                      handleUploadClose();
                    }}
                    disableRipple
                  >
                    Research & Experiments
                  </MenuItem>
                )}
              </StyledMenu>
            </>
          ) : null}
          {getUserRoles().includes("External User") && (
            <Button
              id="upload-file-button"
              onClick={() => handleClickFileUpload("fileUpload")}
              className="btn-upload-file hoverBlue"
              variant="contained"
            >
              File Upload
            </Button>
          )}           
        </Box>
      </Box>

      <StyledTableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>{renderTableCells({} as any, true)}</TableRow>
          </TableHead>
          {loading ? (
            <TableBody>
              {" "}
              <TableRow>
                <TableCell colSpan={10}>
                  <Box className="hvCenter">
                    <CircularProgress size="1.5rem" sx={{ mt: 1.5, mb: 1 }} />
                  </Box>{" "}
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>{renderTableBody()}</TableBody>
          )}
        </Table>
      </StyledTableContainer>

      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={processedData.totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
        className="filterDropdown"
      >
        {activeFilter && (
          <>
            <Box className="Position-relative">
              <TextField
                label={`Filter by ${
                  columns.find((cell) => cell.value === activeFilter)?.label
                }`}
                value={columnSearchTerms[String(activeFilter)] || ""}
                onChange={(e) =>
                  handleColumnSearchChange(String(activeFilter), e.target.value)
                }
                size="small"
              />
              <SearchIcon className="searchIcon" />
            </Box>

            <Box sx={{ mb: 1 }} className="listWrapper">
              <FormGroup>
                {activeFilter !== "isSensitive" &&
                  activeFilter !== "status" &&
                  columnHasNulls(String(activeFilter)) && (
                    <FormControlLabel
                      id={"select empty"}
                      control={
                        <Checkbox
                          id={`select empty`}
                          checked={includeEmptyValues?.[activeFilter] || false}
                          onChange={(event) =>
                            handleEmptyValuesChange(
                              String(activeFilter),
                              event.target.checked
                            )
                          }
                          color="primary"
                        />
                      }
                      label={"(Blanks)"}
                    />
                  )}
                {filteredOptions(String(activeFilter)).map((value: any) => (
                  <FormControlLabel
                    id={`${String(activeFilter)}-${value}`}
                    control={
                      <Checkbox
                        id={`${String(activeFilter)}-${value}`}
                        checked={
                          tempCheckboxFilters[String(activeFilter)]?.[value] ||
                          false
                        }
                        onChange={(event) =>
                          handleTempCheckboxFilterChange(
                            String(activeFilter),
                            value,
                            event
                          )
                        }
                        color="primary"
                      />
                    }
                    label={
                      String(activeFilter) == "fileName" &&
                      String(value).length > 21 ? (
                        <Tooltip arrow title={value}>
                          <span style={{ cursor: "pointer" }}>
                            {String(value).substring(0, 21) + "..."}
                          </span>
                        </Tooltip>
                      ) : String(activeFilter) === "clientStatus" ? (
                        CLIENT_STATUS[value]
                      ) : String(activeFilter) === "lastUpdatedDate" ||
                        String(activeFilter) === "createdDate" ? (
                        new Date(value).toLocaleDateString()
                      ) : (
                        value
                      )
                    }
                  />
                ))}
              </FormGroup>
            </Box>

            <Divider />

            <Box sx={{ mt: "10px" }} className="d-flex justify-content-end">
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setTempCheckboxFilters((prev: any) => ({
                    ...prev,
                    [activeFilter]: {},
                  }));
                  setColumnSearchTerms((prev) => ({
                    ...prev,
                    [activeFilter]: "",
                  }));
                  setIncludeEmptyValues((prev: any) => ({
                    ...prev,
                    [activeFilter]: "",
                  }));
                }}
                sx={{ mr: 1 }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  handleFilterSubmit(String(activeFilter));
                  setFilterMenuAnchor(null); // Close menu after filter is applied
                }}
              >
                Filter
              </Button>
            </Box>
          </>
        )}
      </Menu>

      {openFileUpload && (
        <FileUpload
          handleSave={handleSave}
          openFileUpload={openFileUpload}
          uploadType={fileUploadType || uploadType}
          handlePopUpClose={handlePopUpClose}
          initialFileValues={initialFileValues}
          isEditMode={isEditMode}
          editData={editData}
          fromScreen={fromScreen == "ManagePlatform" ? fromScreen : "TableGrid"}
          industries={industryOptions}
        />
      )}

      {error && (
        <SnackbarComponent
          error={error}
          errorMessage={errorMessage}
          hideError={hideError}
        />
      )}

      {success && (
        <SnackbarSuccessComponent
          success={success}
          successMessage={successMessage}
          hideSuccess={hideSuccess}
        />
      )}
    </TableListStyled>
  );
};

export default OriginalGrid;
